import React from "react";
import { Link } from "react-router-dom";
import { Path } from "../Path";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Article from "@mui/icons-material/Article";
import Money from "@mui/icons-material/Money";
import BusinessIcon from "@mui/icons-material/Business";
import Topic from "@mui/icons-material/Topic";
import Tooltip from "@mui/material/Tooltip";
import Games from "@mui/icons-material/Games";
import Group from "@mui/icons-material/Group";
import Rule from "@mui/icons-material/Rule";
import Sell from "@mui/icons-material/Sell";
import LocalPlay from "@mui/icons-material/LocalPlay";
import InventoryIcon from "@mui/icons-material/Inventory";
import Event from "@mui/icons-material/Event";
import ChecklistIcon from "@mui/icons-material/Checklist";
import TravelExplore from "@mui/icons-material/TravelExplore";
import {
  useGame,
  useGameContracts,
  useGamePlayTests,
  useGamePublishers,
  useGameRoyalties,
  useGameRules,
  useGameSellsheets,
  useGameTopics,
  useGameFiles,
  useGameTasks,
  useMergedGameSessions,
  useGameResearch,
  useMyEntitlements,
} from "@seabrookstudios/pitch2table";
import { useGameIdFromRoute } from "./WithGameIdFromRoute";
import { Features } from "./features";

/**
 * @param {object} props
 * @param {string} props.id
 * @param {import("@mui/material/OverridableComponent").OverridableComponent<import("@mui/material").SvgIconTypeMap<{}, "svg">> & { muiName: string; }} props.Icon
 * @param {string} props.title
 * @param {boolean} [props.disabled]
 * @param {import("react-router-dom").To} props.to
 * @param {number} [props.count]
 */
function QuickLink({ id, Icon, title, to, disabled = false, count }) {
  return (
    <Tooltip title={title}>
      <Box id={id} display="flex" flexDirection="column" alignItems="center">
        <IconButton component={Link} to={to} disabled={disabled} sx={{ flexDirection: "column" }}>
          <Icon fontSize="small" />
          {count !== undefined && (
            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
              }}
            >
              {count}
            </Typography>
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const GamesHome = ({ gameId }) => {
  // For the game overview, we'll just show count of 1 as it represents the game itself
  return <QuickLink id="game-overview" title="Game Overview" Icon={Games} to={Path.game(gameId).link()} count={1} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const PublisherInterest = ({ gameId }) => {
  const { data: publishers = [] } = useGamePublishers(gameId);

  return (
    <QuickLink
      id="publisher-interest"
      title="Publisher Interest"
      Icon={BusinessIcon}
      to={Path.game(gameId).publishers()}
      count={publishers.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Royalties = ({ gameId }) => {
  const { data: royalties = [] } = useGameRoyalties(gameId);

  return (
    <QuickLink
      id="game-royalties"
      title="Royalties"
      Icon={Money}
      to={Path.game(gameId).royalties()}
      count={royalties.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const MessageBoard = ({ gameId }) => {
  const { data: topics = [] } = useGameTopics(gameId);

  return (
    <QuickLink
      id="message-board"
      title="Message Board"
      Icon={Topic}
      to={Path.game(gameId).topics()}
      count={topics.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Files = ({ gameId }) => {
  const { data: files = [] } = useGameFiles(gameId);

  return (
    <QuickLink id="files" title="Files" Icon={InventoryIcon} to={Path.game(gameId).files()} count={files.length} />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Rules = ({ gameId }) => {
  const { data: rules = [] } = useGameRules(gameId);

  return <QuickLink id="game-rules" title="Rules" Icon={Rule} to={Path.game(gameId).rules()} count={rules.length} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const SellSheets = ({ gameId }) => {
  const { data: sellsheets = [] } = useGameSellsheets(gameId);

  return (
    <QuickLink
      id="game-sell-sheets"
      title="Sell Sheets"
      Icon={Sell}
      to={Path.game(gameId).sellsheets()}
      count={sellsheets.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Designers = ({ gameId }) => {
  const { data: game } = useGame(gameId);
  const designers = game?.designers || [];

  return (
    <QuickLink
      id="game-designers"
      title="Designers"
      Icon={Group}
      to={Path.game(gameId).designers()}
      count={designers.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const PlayTests = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  return (
    <QuickLink
      id="play-tests"
      title="Play Tests"
      Icon={LocalPlay}
      to={Path.game(gameId).tests()}
      count={playTests.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Contracts = ({ gameId }) => {
  const { data: contracts = [] } = useGameContracts(gameId);

  return (
    <QuickLink
      id="game-contracts"
      title="Secure Documents"
      Icon={Article}
      to={Path.game(gameId).contracts()}
      count={contracts.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Tasks = ({ gameId }) => {
  const { data: tasks = [] } = useGameTasks(gameId);

  return (
    <QuickLink id="game-tasks" title="Tasks" Icon={ChecklistIcon} to={Path.game(gameId).tasks()} count={tasks.length} />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const Conventions = ({ gameId }) => {
  const { data: conventions = [] } = useMergedGameSessions(gameId);

  return (
    <QuickLink
      id="conventions"
      title="Calendar"
      Icon={Event}
      to={Path.game(gameId).conventions()}
      count={conventions.length}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const GameResearch = ({ gameId }) => {
  const { data: researchGames = [] } = useGameResearch(gameId);

  return (
    <QuickLink
      id="game-research"
      title="Game Research"
      Icon={TravelExplore}
      to={Path.game(gameId).gameResearch()}
      count={researchGames.length}
    />
  );
};

export const GameQuickLinks = () => {
  const gameId = useGameIdFromRoute();
  const { data: entitlements } = useMyEntitlements();
  const isMember = entitlements?.isMember;

  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", justifyContent: { xs: "center", md: "center" } }}>
      <GamesHome gameId={gameId} />
      <Designers gameId={gameId} />
      {isMember && <GameResearch gameId={gameId} />}
      <MessageBoard gameId={gameId} />
      {Features.Tasks && <Tasks gameId={gameId} />}
      {Features.Files && <Files gameId={gameId} />}
      <PlayTests gameId={gameId} />
      <Rules gameId={gameId} />
      <SellSheets gameId={gameId} />
      <PublisherInterest gameId={gameId} />
      <Conventions gameId={gameId} />
      <Contracts gameId={gameId} />
      <Royalties gameId={gameId} />
    </Stack>
  );
};
